<template>
  <DialogWrapper
    class="location-editor-dialog"
    width="60%"
    :loading="dialogLoading"
    :show.sync="showDialog"
    :title="$t(titleI18nKey)"
    :close-on-click-modal="false"
    @open="handleDialogOpen"
    @cancel="handleCancelBtnClick"
    @save="handleSaveBtnClick"
  >
    <div v-loading="loading" class="location-editor-container">
      <div class="location-info">
        <div class="city-info">
          <custom-remote-el-select
            v-model="location.city"
            remote
            filterable
            clearable
            class="info-select city"
            value-key="id"
            :remote-method="searchCity"
            :placeholder="$t(`${i18nPrefix}.placeholder.location.city`)"
            :loading="cityList.loading"
            @change="handleCityChange($event)"
            @visible-change="handleCityVisibleChange"
          >
            <el-option v-for="(city) in cityList.searchResult" :key="city.id" :label="city.i18nName" :value="city"></el-option>
          </custom-remote-el-select>
          <custom-remote-el-select
            v-model="location.region"
            remote
            filterable
            class="info-select region"
            value-key="id"
            :remote-method="searchRegion"
            :placeholder="$t(`${i18nPrefix}.placeholder.location.region`)"
            :loading="regionList.loading"
            @change="handleRegionChange"
            @visible-change="handleRegionVisibleChange"
          >
            <el-option v-for="(region) in regionList.searchResult" :key="region.id" :label="region.i18nName" :value="region"></el-option>
          </custom-remote-el-select>
          <custom-remote-el-select
            v-model="location.cbd"
            remote
            filterable
            class="info-select cbd"
            value-key="id"
            :remote-method="searchCbd"
            :placeholder="$t(`${i18nPrefix}.placeholder.location.cbd`)"
            :loading="cbdList.loading"
            @change="handleCbdChange"
            @visible-change="handleCbdVisibleChange"
          >
            <el-option v-for="(cbd) in cbdList.searchResult" :key="cbd.id" :label="cbd.i18nName" :value="cbd"></el-option>
          </custom-remote-el-select>
        </div>
        <div class="detail-info">
          <el-input
            v-model="location.address"
            class="info-input address"
            :placeholder="$t(`${i18nPrefix}.placeholder.location.address`)"
            @blur="handleAddressChange"
            @keypress.enter.native="handleAddressChange"
          ></el-input>
        </div>
      </div>
      <div class="tip-line" :class="showError && 'has-error'">
        <div v-for="(tip, index) in $t(`${i18nPrefix}.message.locationTip`)" class="line" :key="index">{{ tip }}</div>
      </div>
      <div class="map-container" :id="map.container"></div>
    </div>
  </DialogWrapper>
</template>

<script>
import _ from 'lodash'
import { addI18nProperty } from '@/i18n'
import { mapActions } from 'vuex'
import { getAMap } from '@/plugins/amap'
import DialogWrapper from '@/components/dialog/dialogWrapper.vue'

export default {
  components: {
    DialogWrapper
  },
  props: {
    value: {
      type: Object,
      default: null
    },
    show: {
      type: Boolean,
      default: false
    },
    dialogLoading: {
      type: Boolean,
      default: false
    },
    titleI18nKey: {
      default: 'admin.job.editor.btn.addLocation'
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.job.editor',
      loading: false,
      showError: false,
      oldAddress: null,
      location: {
        city: null,
        region: null,
        cbd: null,
        address: null,
        lng: null,
        lat: null
      },
      cityList: {
        loading: false,
        loader: null,
        data: [],
        searchResult: []
      },
      regionList: {
        loading: false,
        loader: null,
        data: [],
        searchResult: []
      },
      cbdList: {
        loading: false,
        loader: null,
        data: [],
        searchResult: []
      },
      mapLoader: null,
      map: {
        container: 'mapContainer',
        hasInit: false,
        instance: null,
        geoCoder: null,
        marker: null,
        toolbar: null
      },
      searchAddress: null
    }
  },
  computed: {
    showDialog: {
      get () {
        return this.show
      },
      set (val) {
        this.$emit('update:show', val)
      }
    }
  },
  created () {
    this.initMap()
  },
  methods: {
    ...mapActions('worldLocation', ['getCommonCityList', 'getCityRegionList', 'getCityRegionCbdList']),
    async loadAMap () {
      if (!this.mapLoader) {
        const plugins = ['AMap.Geocoder', 'AMap.PlaceSearch', 'AMap.ToolBar']
        this.mapLoader = getAMap(plugins)
      }
      return this.mapLoader
    },
    async initMap () {
      const AMap = await this.loadAMap()
      const markerIcon = new AMap.Icon({
        image: require('@/assets/amap/poi-marker-red.png'),
        imageSize: new AMap.Size(25, 34)
      })
      const marker = new AMap.Marker({
        icon: markerIcon,
        offset: new AMap.Pixel(-13, -30),
        draggable: true,
        cursor: 'move'
      })
      marker.on('dragend', (event) => {
        const lngLat = event.lnglat
        this.setLocationInfo(lngLat)
      })
      this.map.geoCoder = new AMap.Geocoder()
      this.map.marker = marker
      this.map.toolbar = new AMap.ToolBar()
      this.map.hasInit = true
    },
    async renderMap () {
      if (!this.showDialog) {
        return
      }
      this.loading = true
      if (!this.map.instance) {
        const AMap = await this.loadAMap()
        if (!this.map.hasInit) {
          await this.initMap()
        }
        const mapInstance = new AMap.Map(this.map.container, { zoom: 15 })
        mapInstance.addControl(this.map.toolbar)
        mapInstance.on('click', (event) => {
          const lngLat = event.lnglat
          this.setLocationInfo(lngLat)
          this.setMapMarkerPosition(lngLat)
        })
        this.map.instance = mapInstance
      }
      const locationInfo = this.value || {}
      const location = {
        city: locationInfo.city,
        region: locationInfo.region,
        cbd: locationInfo.cbd,
        address: locationInfo.address,
        lng: locationInfo.lng,
        lat: locationInfo.lat
      }
      this.location = location
      this.oldAddress = location.address
      if (this.value && this.value.lng && this.value.lat) {
        this.setMapMarkerPosition([location.lng, location.lat])
      }
      this.loading = false
    },
    setMapMarkerPosition (lngLat) {
      const map = this.map
      const mapInstance = map.instance
      const marker = map.marker
      if (!marker.getMap()) {
        marker.setMap(mapInstance)
      }
      mapInstance.setCenter(lngLat)
      marker.setPosition(lngLat)
    },
    async setLocationInfo (lngLat) {
      this.loading = true
      this.map.geoCoder.getAddress(lngLat, async (status, result) => {
        if (status !== 'complete' && result.info !== 'OK') {
          return
        }
        const info = result.regeocode
        this.convertAddressComponent(info.addressComponent, info.formattedAddress, true)
        this.location.lng = lngLat.lng
        this.location.lat = lngLat.lat
        this.loading = false
      })
    },
    resetMapMarker () {
      this.loading = true
      const location = this.location
      const province = (location.city?.parent.cn_name || '').trim()
      const city = (location.city?.cn_name || '').trim()
      const region = (location.region?.cn_name || '').trim()
      let address = (location.address || '').trim()
      let formattedAddress = ''
      if (province) {
        formattedAddress += province
        if (address.startsWith(province)) {
          address = address.substring(province.length)
        }
      }
      if (city && city !== province) {
        formattedAddress += city
        if (address.startsWith(city)) {
          address = address.substring(city.length)
        }
      }
      if (region) {
        formattedAddress += region
        if (address.startsWith(region)) {
          address = address.substring(region.length)
        }
      }
      formattedAddress += address
      if (!formattedAddress) {
        return
      }
      const map = this.map
      if (province) {
        let searchProvince = province
        if (searchProvince.startsWith('香港')) {
          searchProvince = '香港特别行政区'
        } else if (searchProvince.startsWith('澳门')) {
          searchProvince = '澳门特别行政区'
        } else if (searchProvince.startsWith('台湾')) {
          searchProvince = '台湾省'
        }
        map.geoCoder.setCity(searchProvince)
      } else {
        map.geoCoder.setCity()
      }
      const timeoutError = setTimeout(() => {
        this.$message.error(this.$t(`${this.i18nPrefix}.message.resolveAddressError`))
        this.loading = false
      }, 5000)

      map.geoCoder.getLocation(formattedAddress, async (status, result) => {
        clearTimeout(timeoutError)
        if (status !== 'complete' && result.info !== 'OK') {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.resolveAddressError`))
          this.loading = false
          return
        }
        const locationInfo = result.geocodes[0]
        if (!locationInfo) {
          this.$message.error(this.$t(`${this.i18nPrefix}.message.resolveAddressError`))
          this.loading = false
          return
        }
        const addressComponent = locationInfo.addressComponent
        const lngLat = locationInfo.location
        await this.convertAddressComponent(addressComponent, locationInfo.formattedAddress, false)
        this.setMapMarkerPosition(lngLat)
        this.location.lng = lngLat.lng
        this.location.lat = lngLat.lat
        this.loading = false
      })
    },
    async convertAddressComponent (addressComponent, formattedAddress, clearWhenNoMatch) {
      if (!addressComponent) {
        return
      }
      let isTaiWan = false
      let province = addressComponent.province
      let city = addressComponent.city
      let region = addressComponent.district
      if (!city || city.length < 1) {
        if (province.endsWith('市')) {
          city = province
        } else if (region.endsWith('市')) {
          city = region
          region = addressComponent.township
        }
      }
      const cbdList = addressComponent.businessAreas
      const street = addressComponent.street
      const streetNumber = addressComponent.streetNumber
      if (province.startsWith('澳门')) {
        province = '澳门'
        if (!city || city.length < 1) {
          city = province
        }
      } else if (province.startsWith('香港')) {
        province = '香港'
        if (!city || city.length < 1) {
          city = province
        }
      } else if (province.startsWith('台湾')) {
        province = '台湾'
        city = city || this.location.city?.cn_name
        isTaiWan = true
      } else if (province === '新疆维吾尔自治区') {
        province = '新疆自治区'
      } else if (province === '宁夏回族自治区') {
        province = '宁夏自治区'
      } else if (province === '广西壮族自治区') {
        province = '广西自治区'
      }

      await this.loadLocationData(this.cityList)
      const cityInfo = _.find(this.cityList.data, cityInfoItem => {
        let cityName = city
        if (cityName?.endsWith('市')) {
          cityName = cityName.substring(0, cityName.indexOf('市'))
        }
        return (cityInfoItem.cn_name === city || cityInfoItem.cn_name === cityName) && cityInfoItem.parent.cn_name === province
      })
      if (cityInfo && this.location.city?.id !== cityInfo.id) {
        this.location.city = cityInfo
        await this.loadLocationData(this.regionList, true)
      } else if (!cityInfo && clearWhenNoMatch && !isTaiWan) {
        this.location.city = null
        this.location.region = null
        this.location.cbd = null
        this.regionList.data = this.regionList.searchResult = []
        this.cbdList.data = this.cbdList.searchResult = []
        return
      }

      const regionInfo = _.find(this.regionList.data, { cn_name: region })
      if (regionInfo && this.location.region?.id !== regionInfo.id) {
        this.location.region = regionInfo
        await this.loadLocationData(this.cbdList, true)
      } else if (!regionInfo && clearWhenNoMatch && !isTaiWan) {
        this.location.region = null
        this.cbdList.data = this.cbdList.searchResult = []
      }
      this.location.cbd = null
      _.forEach(cbdList, cbd => {
        const cbdInfo = _.find(this.cbdList.data, { cn_name: cbd.name })
        if (cbdInfo) {
          this.location.cbd = cbdInfo
        }
        return !cbdInfo
      })
      if (isTaiWan && !clearWhenNoMatch) {
        return
      }
      let address = null
      if (region && street) {
        address = `${region}${street}${streetNumber}`
      } else {
        address = formattedAddress
        const originProvince = addressComponent.province
        if (originProvince && address.startsWith(originProvince)) {
          address = address.substring(originProvince.length)
        }
        const selectCityName = this.location.city?.cn_name
        if (city && address.startsWith(city)) {
          address = address.substring(city.length)
        } else if (!city && address.startsWith(region)) {
          address = address.substring(region.length)
        }
        if (selectCityName && address.startsWith(selectCityName)) {
          address = address.substring(selectCityName.length)
        }
      }
      if (this.searchAddress) {
        address = this.searchAddress
        this.searchAddress = null
      }
      this.location.address = address
      this.oldAddress = address
    },
    addI18nName (obj) {
      return addI18nProperty(obj, 'i18nName', 'cn_name', 'name')
    },
    async loadLocationData (listInfo, reload) {
      if (reload || !listInfo.loader) {
        listInfo.loading = true
        let loadFunc = this.getCommonCityList
        let params = { need_parent: '1' }
        if (listInfo === this.regionList) {
          loadFunc = this.getCityRegionList
          params = { cityId: this.location.city.id }
        } else if (listInfo === this.cbdList) {
          loadFunc = this.getCityRegionCbdList
          params = { cityId: this.location.city.id, regionId: this.location.region.id }
        }
        listInfo.loader = loadFunc(params).then(resp => {
          if (listInfo === this.cityList) {
            listInfo.searchResult = listInfo.data = _.chain(resp).map(city => {
              this.addI18nName(city.parent)
              addI18nProperty(city, '_i18nName', 'cn_name', 'name')
              Object.defineProperty(city, 'i18nName', {
                configurable: true,
                enumerable: true,
                get () {
                  return `${this.parent.i18nName} --- ${this._i18nName}`
                }
              })
              city.searchName = _.toUpper(`${city.pinyin_name}|${city.cn_name}|${city.name}`)
              city.parent.searchName = _.toUpper(`${city.parent.pinyin_name}|${city.parent.cn_name}|${city.parent.name}`)
              return city
            }).sortBy('parent.pinyin_name').value()
          } else {
            listInfo.searchResult = listInfo.data = _.map(resp, (item) => {
              item.searchName = _.toUpper(`${item.pinyin_name}|${item.cn_name}|${item.name}`)
              return this.addI18nName(item)
            })
          }
        }).finally(() => {
          listInfo.loading = false
        })
      }
      return listInfo.loader
    },
    locationInfoFilter (location, keywords) {
      return _.includes(location.searchName, _.toUpper(keywords))
    },
    async searchLocationInfo (listInfo, keywords) {
      listInfo.loading = true
      if (keywords) {
        listInfo.searchResult = _.filter(listInfo.data, (item) => {
          let found = this.locationInfoFilter(item, keywords)
          if (!found) {
            const parent = item.parent
            if (parent) {
              found = this.locationInfoFilter(parent, keywords)
            }
          }
          return found
        })
      } else {
        listInfo.searchResult = listInfo.data
      }
      listInfo.loading = false
    },
    searchCity (keywords) {
      this.searchLocationInfo(this.cityList, keywords)
    },
    searchRegion (keywords) {
      this.searchLocationInfo(this.regionList, keywords)
    },
    searchCbd (keywords) {
      this.searchLocationInfo(this.cbdList, keywords)
    },
    handleCityChange (city) {
      this.showError = false
      this.location.region = null
      this.location.cbd = null
      this.location.address = null
      if (!city) {
        return
      }
      this.loadLocationData(this.regionList, true)
      this.resetMapMarker()
    },
    handleRegionChange () {
      this.location.cbd = null
      this.location.address = null
      this.loadLocationData(this.cbdList, true)
      this.resetMapMarker()
    },
    handleCbdChange () {

    },
    handleAddressChange () {
      if (!this.location.address || this.oldAddress === this.location.address) {
        return
      }
      this.showError = false
      this.searchAddress = this.location.address
      this.resetMapMarker()
      this.oldAddress = this.location.address
    },
    handleCityVisibleChange (visible) {
      if (!visible) {
        this.$nextTick(() => {
          this.cityList.searchResult = this.cityList.data
        })
      }
    },
    handleRegionVisibleChange (visible) {
      if (!visible) {
        this.$nextTick(() => {
          this.regionList.searchResult = this.regionList.data
        })
      }
    },
    handleCbdVisibleChange (visible) {
      if (!visible) {
        this.$nextTick(() => {
          this.cbdList.searchResult = this.cbdList.data
        })
      }
    },
    handleDialogOpen () {
      this.$nextTick(() => {
        this.$children[0].$el.scrollTo({ top: 0, left: 0 })
      })
    },
    handleSaveBtnClick () {
      const location = this.location
      if (!location.lng || !location.lat || !location.city || !location.address) {
        this.showError = true
        this.$el.querySelector('.tip-line').scrollIntoView({ block: 'center', behavior: 'smooth' })
        return
      }
      this.$emit('change', { ...location, city: this.addI18nName({ ...location.city }) })
    },
    handleCancelBtnClick () {
      this.showDialog = false
    }
  },
  watch: {
    showDialog: {
      immediate: true,
      handler () {
        if (this.showDialog) {
          this.loadLocationData(this.cityList)
          if (this.value) {
            this.location = this.value
            if (this.value?.city?.id) {
              this.loadLocationData(this.regionList, true)
            }
            if (this.value?.region?.id) {
              this.loadLocationData(this.cbdList, true)
            }
          }
          this.renderMap()
        } else {
          this.loading = false
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.location-editor-dialog {
  .location-editor-container {
    .location-info {
      .city-info {
        display: flex;

        .info-select {
          width: auto;

          &.city {
            flex: 1;
          }

          &.region {
            width: 25%;
            margin-left: 20px;
          }

          &.cbd {
            width: 25%;
            margin-left: 20px;
          }
        }
      }

      .detail-info {
        margin-top: 20px;
      }
    }

    .tip-line {
      margin-top: 5px;
      font-size: 12px;

      &.has-error {
        color: #F56C6C;
      }
    }

    .map-container {
      margin-top: 15px;
      height: 300px;
    }
  }

  .footer-btn-group {
    display: flex;
    justify-content: space-between;

    .footer-btn {
      padding: 10px 25px;
    }
  }
}
</style>
